if (!customElements.get('back-in-stock')) {
  customElements.define(
    'back-in-stock',
    class BackInStock extends HTMLElement {
      // Klaviyo client API version, see https://developers.klaviyo.com/en/reference/api_overview#versioning
      static REVISION = '2024-10-15';

      connectedCallback() {
        // Delegated: product-info.js replaces the contents when the variant changes.
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('submit', this.onSubmit.bind(this));
      }

      get dialog() {
        return this.querySelector('[data-restock-dialog]');
      }

      onClick(event) {
        if (event.target.closest('[data-restock-open]')) {
          this.open();
        } else if (event.target.closest('[data-restock-close]') || event.target === this.dialog) {
          // The dialog itself is only the target when the backdrop is clicked.
          this.dialog.close();
        }
      }

      open() {
        const dialog = this.dialog;
        if (!dialog) return;

        this.showStep(false);
        this.setError('');
        dialog.showModal();
        document.body.classList.add('overflow-hidden');
        dialog.addEventListener('close', () => document.body.classList.remove('overflow-hidden'), { once: true });
      }

      async onSubmit(event) {
        const form = event.target.closest('[data-restock-form]');
        if (!form) return;
        event.preventDefault();

        const input = form.elements.email;
        const email = input.value.trim();
        if (!email || !input.checkValidity()) {
          this.setError(form.dataset.errorEmail);
          input.focus();
          return;
        }

        const { companyId, listId } = this.dataset;
        if (!companyId) {
          console.error('Back in stock: set the Klaviyo public API key in the product section settings.');
          this.setError(form.dataset.errorGeneric);
          return;
        }

        this.setError('');
        this.setLoading(form, true);

        try {
          await this.post('back-in-stock-subscriptions', {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: { data: { type: 'profile', attributes: { email } } },
            },
            relationships: {
              variant: {
                data: { type: 'catalog-variant', id: `$shopify:::$default:::${this.dialog.dataset.variantId}` },
              },
            },
          });

          // The back in stock request already succeeded, so a failed list signup doesn't block the success step.
          if (listId && form.elements.newsletter?.checked) {
            await this.post('subscriptions', {
              type: 'subscription',
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } },
                  },
                },
              },
              relationships: { list: { data: { type: 'list', id: listId } } },
            }).catch((error) => console.error('Back in stock: email list signup failed', error));
          }

          this.renderSuccessText(email);
          this.showStep(true);
        } catch (error) {
          console.error('Back in stock: subscription failed', error);
          this.setError(form.dataset.errorGeneric);
        } finally {
          this.setLoading(form, false);
        }
      }

      post(endpoint, data) {
        const url = `https://a.klaviyo.com/client/${endpoint}?company_id=${encodeURIComponent(this.dataset.companyId)}`;
        return fetch(url, {
          method: 'POST',
          headers: {
            accept: 'application/vnd.api+json',
            'content-type': 'application/vnd.api+json',
            revision: BackInStock.REVISION,
          },
          body: JSON.stringify({ data }),
        }).then((response) => {
          if (!response.ok) throw new Error(`Klaviyo responded with ${response.status}`);
        });
      }

      // Fills the success text, with [email] and [product] highlighted.
      renderSuccessText(email) {
        const target = this.querySelector('[data-restock-success-text]');
        if (!target) return;

        const values = { '[email]': email, '[product]': this.dialog.dataset.productName };
        const parts = target.dataset.template.split(/(\[email\]|\[product\])/);
        target.replaceChildren(
          ...parts.map((part) => {
            if (!(part in values)) return document.createTextNode(part);
            const highlight = document.createElement('span');
            highlight.className = 'restock-modal__highlight';
            highlight.textContent = values[part];
            return highlight;
          })
        );
      }

      showStep(success) {
        this.querySelector('[data-restock-form]').hidden = success;
        this.querySelector('[data-restock-success]').hidden = !success;
        if (success) this.querySelector('[data-restock-success] [data-restock-close]')?.focus();
      }

      setError(message) {
        const error = this.querySelector('.restock-modal__error');
        if (!error) return;
        error.textContent = message;
        error.hidden = !message;
        this.querySelector('.restock-modal__input')?.setAttribute('aria-invalid', message ? 'true' : 'false');
      }

      setLoading(form, loading) {
        const button = form.querySelector('[type="submit"]');
        button.disabled = loading;
        button.classList.toggle('loading', loading);
        button.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
      }
    }
  );
}
